import { useState } from 'react'
import { formatClock, formatDuration } from '../lib/format'
import type { Block, Category } from '../data/types'

interface Props {
  block: Block
  category: Category | undefined
  now: number
  onSplit: (at: number) => void
  onEnd: (at: number) => void
  onDismiss: () => void
}

/**
 * The "I left steer-ai running through lunch" prompt. Offers the two repairs
 * that are actually wanted: cut it here, or say when it really ended.
 */
export function StaleBanner({ block, category, now, onSplit, onEnd, onDismiss }: Props) {
  const [endValue, setEndValue] = useState(() => formatClock(new Date(now)))

  function applyEnd() {
    const [hours, minutes] = endValue.split(':').map(Number)
    if (hours === undefined || minutes === undefined || Number.isNaN(hours)) return
    const at = new Date(block.start)
    at.setHours(hours, minutes, 0, 0)
    // A time earlier than the start means it ran past midnight into today.
    if (at.getTime() <= block.start) at.setDate(at.getDate() + 1)
    onEnd(at.getTime())
  }

  return (
    <section className="mb-3 rounded-xl border border-line-strong bg-raised px-3 py-2.5">
      <div className="flex items-start gap-2">
        <p className="flex-1 text-[13px] leading-relaxed text-text">
          Still on <strong className="font-semibold">{category?.label ?? block.categoryId}</strong>{' '}
          after {formatDuration(now - block.start)}. Was it really?
        </p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mt-0.5 -mr-1 px-1.5 text-[17px] leading-none text-faint hover:text-text"
        >
          ×
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-2">
        <button
          type="button"
          onClick={() => onSplit(now)}
          className="rounded-md border border-line px-2 py-1 text-[13px] font-medium text-dim transition-colors duration-150 hover:border-line-strong hover:text-text"
        >
          Split here
        </button>
        <span className="flex items-center gap-1.5">
          <input
            type="time"
            value={endValue}
            onChange={(event) => setEndValue(event.target.value)}
            aria-label="Ended at"
            className="rounded-md border border-line bg-bg px-2 py-1 text-[13px] tnum outline-none focus:border-line-strong"
          />
          <button
            type="button"
            onClick={applyEnd}
            className="rounded-md border border-line px-2 py-1 text-[13px] font-medium text-dim transition-colors duration-150 hover:border-line-strong hover:text-text"
          >
            Ended then
          </button>
        </span>
      </div>
    </section>
  )
}
